import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import { Grid, Header, Button, Icon, Message, Segment, Divider, Container } from 'semantic-ui-react';
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikField,
  FormikCheckbox
} from '../../pages/subPages/voiceActor/FormComponents';
import {
  fetchSecurityDefinition,
  createSecurityDefinition,
  updateSecurityDefinition,
  validateSecurityDefinition,
  clearError,
  selectCurrentSecurityDefinition,
  selectIsLoading,
  selectError,
  selectValidationResult,
  selectIsValidating
} from '../../slices/securityRegistrySlice';

import { AuthStdFooter } from "../../utils/auth-std-footer";
const exampleJSONValidationRulesFormat = `{
  "validations": {
    "email": {
      "type": "email",
      "required": true,
      "description": "Valid email address"
    },
    "amount": {
      "type": "currency",
      "required": true,
      "min": 0,
      "max": 1000000,
      "description": "Amount in USD"
    }
  }
}`


const exampleJSONAcessControlRules = `{
  "operations": {
    "CREATE": {
      "requiresRole": ["admin", "user"],
      "requiresEncryption": true,
      "auditLevel": "FULL"
    },
    "READ": {
      "requiresRole": ["admin", "user"],
      "auditLevel": "BASIC"
    }
  }
}`
/**
 * SecurityDefinitionForm component for creating and editing security definitions
 * Mirrors PathDefinitionForm structure for consistency
 * @returns {JSX.Element} SecurityDefinitionForm component
 */
const SecurityDefinitionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { name } = useParams();
  const isEditing = !!name;

  // Get security definition data from Redux
  const currentSecurityDefinition = useSelector(selectCurrentSecurityDefinition);
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectError);
  const validationResult = useSelector(selectValidationResult);
  const validationLoading = useSelector(selectIsValidating);

  // Local state for form management
  const [formLoading, setFormLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(null);
  const [showValidationDetails, setShowValidationDetails] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  console.log('[VALIDATION ERROR from slice', error);

  // Fetch security definition data if editing
  useEffect(() => {
    if (isEditing && name) {
      dispatch(fetchSecurityDefinition(name));
    }
  }, [dispatch, isEditing, name]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
      // setTimeout(() => {
      //   dispatch(clearError());
      //   setLocalError(null);
      // }, 5000);
    }
  }, [dispatch, error]);

  // Run server validation when security definition loads (for editing)
  useEffect(() => {
    if (isEditing && currentSecurityDefinition) {
      dispatch(validateSecurityDefinition(currentSecurityDefinition));
    }
  }, [dispatch, isEditing, currentSecurityDefinition]);

  // Store initial values for change detection
  useEffect(() => {
    const initialVals = getInitialValues();
    if (!initialValues || JSON.stringify(initialValues) !== JSON.stringify(initialVals)) {
      setInitialValues(initialVals);
      setHasChanges(false); // Reset changes when initial values change
    }
  }, [currentSecurityDefinition, isEditing]);

  // Helper function to compare current values with initial values
  const checkForChanges = (currentValues) => {
    if (!initialValues) return false;

    // Deep comparison of form values
    const hasFormChanges = JSON.stringify(currentValues) !== JSON.stringify(initialValues);
    setHasChanges(hasFormChanges);
    return hasFormChanges;
  };

  // Prepare initial form values
  const getInitialValues = () => {
    if (isEditing && currentSecurityDefinition) {
      // Convert operations object to accessControl array format for editing
      let accessControl = [];
      if (currentSecurityDefinition.operations) {
        accessControl = Object.entries(currentSecurityDefinition.operations).map(([operation, config]) => {
          // Convert boolean properties to advancedRules array
          const advancedRules = [];
          if (config.requiresApproval) advancedRules.push('requiresApproval');
          if (config.requiresEncryption) advancedRules.push('requiresEncryption');
          if (config.checksumValidation) advancedRules.push('checksumValidation');
          if (config.versionControl) advancedRules.push('versionControl');
          if (config.dataFiltering) advancedRules.push('dataFiltering');

          return {
            operation: operation,
            requiredRoles: config.requiresRole || config.requiredRoles || [],
            description: `${operation} operation rules`,
            advancedRules: advancedRules,
            auditLevel: config.auditLevel || 'BASIC'
          };
        });
      } else if (currentSecurityDefinition.accessControl) {
        accessControl = currentSecurityDefinition.accessControl;
      }

      // Convert validations object to validationRules array format for editing
      let validationRules = [];
      if (currentSecurityDefinition.validations) {
        validationRules = Object.entries(currentSecurityDefinition.validations).map(([field, config]) => ({
          field: field,
          type: config.type?.toUpperCase() || 'REQUIRED',
          parameters: JSON.stringify({
            required: config.required,
            min: config.min,
            max: config.max,
            maxLength: config.maxLength,
            pattern: config.pattern,
            values: config.values
          }),
          message: config.description || `Validation rule for ${field}`
        }));
      } else if (currentSecurityDefinition.validationRules) {
        validationRules = currentSecurityDefinition.validationRules;
      }

      return {
        ...currentSecurityDefinition,
        // Use converted arrays
        accessControl: accessControl,
        validationRules: validationRules,
        // Convert tags array to comma-separated string for form display
        tags: Array.isArray(currentSecurityDefinition.tags)
          ? currentSecurityDefinition.tags.join(', ')
          : currentSecurityDefinition.tags || '',
        // Ensure audit config exists
        auditConfig: {
          enabled: false,
          level: 'STANDARD',
          retentionDays: 90,
          includeSensitiveData: false,
          ...currentSecurityDefinition.auditConfig
        }
      };
    }

    // Default values for new security definition
    return {
      name: '',
      description: '',
      entityType: '',
      criticality: 'MEDIUM',
      active: true,
      version: '1.0.0',
      accessControl: [],
      validationRules: [],
      auditConfig: {
        enabled: false,
        level: 'STANDARD',
        retentionDays: 90,
        includeSensitiveData: false
      },
      tags: ''
    };
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setFormLoading(true);
    setLocalError(null);
    setLocalSuccess(null);

    try {
      // Transform accessControl array to operations object format
      const operations = {};
      if (values.accessControl && values.accessControl.length > 0) {
        values.accessControl.forEach(rule => {
          if (rule.operation) {
            // Convert advancedRules array back to individual boolean properties
            const advancedRules = rule.advancedRules || [];

            operations[rule.operation] = {
              requiresRole: rule.requiredRoles || [],
              auditLevel: rule.auditLevel || 'BASIC',
              requiresEncryption: advancedRules.includes('requiresEncryption'),
              requiresApproval: advancedRules.includes('requiresApproval'),
              checksumValidation: advancedRules.includes('checksumValidation'),
              versionControl: advancedRules.includes('versionControl'),
              cascadeValidation: false, // Not currently in UI
              dataFiltering: advancedRules.includes('dataFiltering')
            };
          }
        });
      }

      // Transform validationRules array to validations object format
      const validations = {};
      if (values.validationRules && values.validationRules.length > 0) {
        values.validationRules.forEach(rule => {
          if (rule.field) {
            let parameters = {};
            try {
              parameters = rule.parameters ? JSON.parse(rule.parameters) : {};
            } catch (e) {
              console.warn('Invalid JSON in validation parameters:', rule.parameters);
            }

            validations[rule.field] = {
              type: rule.type?.toLowerCase() || 'string',
              required: parameters.required || false,
              description: rule.message || `Validation for ${rule.field}`,
              ...parameters
            };
          }
        });
      }

      // Prepare data for submission in the format expected by server
      const securityDefData = {
        name: values.name,
        description: values.description || '',
        active: values.active !== undefined ? values.active : true,
        entityType: values.entityType,
        criticality: values.criticality,
        version: values.version || '1.0.0',
        operations: operations,
        validations: validations,
        // Convert tags from comma-separated string to array if needed
        tags: typeof values.tags === 'string'
          ? values.tags.split(',').map(tag => tag.trim()).filter(Boolean)
          : values.tags || [],
        // Include audit config if present
        auditConfig: values.auditConfig || {
          enabled: false,
          level: 'STANDARD',
          retentionDays: 90,
          includeSensitiveData: false
        }
      };

      // Pre-save validation: validate data before attempting to save
      console.log('Running pre-save validation...');
      const preValidationResult = await dispatch(validateSecurityDefinition(securityDefData)).unwrap();

      // Block save if validation status is INVALID
      if (preValidationResult.status === 'INVALID') {
        const issuesList = preValidationResult.issues?.join(', ') || 'Unknown validation issues';
        setLocalError(`Cannot save: Critical validation issues must be resolved first. Issues: ${issuesList}`);
        return;
      }

      // Log validation result for debugging
      console.log('Pre-save validation result:', preValidationResult.status, 'Score:', preValidationResult.score);

      if (isEditing) {
        // Update existing security definition
        await dispatch(updateSecurityDefinition({
          name,
          definition: securityDefData
        })).unwrap();
        setLocalSuccess('Security definition updated successfully');

        // Re-run validation after successful save to update display
        dispatch(validateSecurityDefinition(securityDefData));
      } else {
        // Create new security definition
        await dispatch(createSecurityDefinition(securityDefData)).unwrap();
        setLocalSuccess('Security definition created successfully');
        resetForm();
      }
    } catch (err) {
      console.error('Save error:', err);
      // setLocalError(err.message || 'An error occurred while saving');
      setLocalError(error);
    } finally {
      setFormLoading(false);
      setSubmitting(false);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    navigate('/admin/security-registry');
  };

  // Helper function to process Access Control Rules JSON
  const processAccessControlJson = (jsonText, setFieldValue) => {
    try {
      const parsedJson = JSON.parse(jsonText);
      if (parsedJson.operations) {
        // Convert operations object to access control array format
        const accessControlRules = Object.entries(parsedJson.operations).map(([operation, config]) => ({
          operation: operation,
          requiredRoles: config.requiresRole || [],
          conditions: [],
          description: `${operation} operation rules`,
          requiresEncryption: config.requiresEncryption || false,
          auditLevel: config.auditLevel || 'BASIC',
          requiresApproval: config.requiresApproval || false,
          checksumValidation: config.checksumValidation || false,
          versionControl: config.versionControl || false,
          cascadeValidation: config.cascadeValidation || false,
          dataFiltering: config.dataFiltering || false
        }));
        setFieldValue('accessControl', accessControlRules);
        setLocalSuccess('Access Control Rules imported successfully from JSON!');
      } else {
        setLocalError('Invalid JSON format. Expected "operations" object.');
      }
    } catch (error) {
      setLocalError('Invalid JSON format. Please check your JSON syntax.');
    }
  };

  // Helper function to process Validation Rules JSON
  const processValidationJson = (jsonText, setFieldValue) => {
    try {
      const parsedJson = JSON.parse(jsonText);
      if (parsedJson.validations) {
        // Convert validations object to validation rules array format
        const validationRules = Object.entries(parsedJson.validations).map(([field, config]) => ({
          field: field,
          type: config.type?.toUpperCase() || 'REQUIRED',
          parameters: JSON.stringify({
            required: config.required,
            min: config.min,
            max: config.max,
            maxLength: config.maxLength,
            pattern: config.pattern,
            values: config.values
          }),
          message: config.description || `Validation rule for ${field}`
        }));
        setFieldValue('validationRules', validationRules);
        setLocalSuccess('Validation Rules imported successfully from JSON!');
      } else {
        setLocalError('Invalid JSON format. Expected "validations" object.');
      }
    } catch (error) {
      setLocalError('Invalid JSON format. Please check your JSON syntax.');
    }
  };

  // Options for dropdowns
  const entityTypeOptions = [
    { key: 'opportunity', text: 'Opportunity', value: 'Opportunity' },
    { key: 'application', text: 'Application', value: 'Application' },
    { key: 'message', text: 'Message', value: 'Message' },
    { key: 'bookprofile', text: 'BookProfile', value: 'BookProfile' }
  ];

  const criticalityOptions = [
    { key: 'critical', text: 'CRITICAL', value: 'CRITICAL' },
    { key: 'high', text: 'HIGH', value: 'HIGH' },
    { key: 'medium', text: 'MEDIUM', value: 'MEDIUM' },
    { key: 'low', text: 'LOW', value: 'LOW' }
  ];

  const operationOptions = [
    { key: 'create', text: 'CREATE', value: 'CREATE' },
    { key: 'read', text: 'READ', value: 'READ' },
    { key: 'update', text: 'UPDATE', value: 'UPDATE' },
    { key: 'delete', text: 'DELETE', value: 'DELETE' },
    { key: 'list', text: 'LIST', value: 'LIST' }
  ];

  const roleOptions = [
    { key: 'admin', text: 'admin', value: 'admin' },
    { key: 'financial', text: 'financial', value: 'financial' },
    { key: 'user', text: 'user', value: 'user' }
  ];

  const validationTypeOptions = [
    { key: 'required', text: 'REQUIRED', value: 'REQUIRED' },
    { key: 'format', text: 'FORMAT', value: 'FORMAT' },
    { key: 'range', text: 'RANGE', value: 'RANGE' },
    { key: 'custom', text: 'CUSTOM', value: 'CUSTOM' }
  ];

  const auditLevelOptions = [
    { key: 'minimal', text: 'MINIMAL', value: 'MINIMAL' },
    { key: 'standard', text: 'STANDARD', value: 'STANDARD' },
    { key: 'detailed', text: 'DETAILED', value: 'DETAILED' },
    { key: 'comprehensive', text: 'COMPREHENSIVE', value: 'COMPREHENSIVE' }
  ];

  const advancedRulesOptions = [
    { key: 'requiresApproval', text: 'Requires Approval', value: 'requiresApproval' },
    { key: 'requiresEncryption', text: 'Requires Encryption', value: 'requiresEncryption' },
    { key: 'checksumValidation', text: 'Checksum Validation', value: 'checksumValidation' },
    { key: 'versionControl', text: 'Version Control', value: 'versionControl' },
    { key: 'dataFiltering', text: 'Data Filtering', value: 'dataFiltering' }
  ];

  // Helper function to get smart defaults for advanced rules based on operation and criticality
  const getAdvancedRulesDefaults = (operation, criticality) => {
    const defaults = [];

    switch (operation) {
      case 'CREATE':
        // Basic operations - no defaults
        break;
      case 'READ':
        defaults.push('dataFiltering'); // For privacy
        break;
      case 'UPDATE':
        if (criticality === 'CRITICAL' || criticality === 'HIGH') {
          defaults.push('requiresApproval');
        }
        break;
      case 'DELETE':
        defaults.push('requiresApproval', 'versionControl'); // For safety
        if (criticality === 'CRITICAL') {
          defaults.push('requiresEncryption');
        }
        break;
      case 'LIST':
        defaults.push('dataFiltering'); // For privacy
        break;
    }

    // Additional defaults based on criticality
    if (criticality === 'CRITICAL') {
      if (operation === 'CREATE' || operation === 'UPDATE') {
        if (!defaults.includes('requiresEncryption')) {
          defaults.push('requiresEncryption');
        }
      }
    }

    return defaults;
  };

  return (
    <div className="page-container" style={{ marginBottom: "70px" }}>
      <Container>
        <Segment>
          <Header as="h2">
            <Icon name="shield alternate" />
            <Header.Content>
              {isEditing ? 'Edit Security Definition' : 'Create New Security Definition'}
              <Header.Subheader>
                {isEditing
                  ? `Editing security definition: ${name}`
                  : 'Create a new security definition for access control and validation'}
              </Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          {/* Navigation buttons */}
          <div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon
              labelPosition="left"
              onClick={handleCancel}
            >
              <Icon name="arrow left" />
              Back to Security Registry
            </Button>
            {isEditing && (
              <Button
                as={Link}
                to={`/admin/security-registry/${name}/view`}
                color="blue"
                icon
                labelPosition="left"
              >
                <Icon name="eye" />
                View Details
              </Button>
            )}
          </div>

          {/* Validation Status Banner - show when editing and validation result exists */}
          {isEditing && validationResult && (
            <Message
              color={validationResult.color}
              icon
              style={{ marginBottom: '1.5em' }}
            >
              <Icon name={validationResult.icon} />
              <Message.Content>
                <Message.Header>
                  Current Status: {validationResult.status}
                  <span style={{ float: 'right', fontSize: '0.9em', fontWeight: 'normal' }}>
                    Score: {validationResult.score}/100
                  </span>
                </Message.Header>
                <p>{validationResult.message}</p>

                {(validationResult.issues?.length > 0 || validationResult.warnings?.length > 0) && (
                  <div style={{ marginTop: '0.5em' }}>
                    <Button
                      size="mini"
                      basic
                      onClick={() => setShowValidationDetails(!showValidationDetails)}
                      style={{ marginBottom: '0.5em' }}
                    >
                      <Icon name={showValidationDetails ? 'chevron up' : 'chevron down'} />
                      {showValidationDetails ? 'Hide' : 'Show'} Details
                    </Button>

                    {showValidationDetails && (
                      <div style={{
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        padding: '0.8em',
                        borderRadius: '4px',
                        marginTop: '0.5em'
                      }}>
                        {validationResult.issues?.length > 0 && (
                          <div style={{ marginBottom: '0.5em' }}>
                            <strong style={{ color: '#db2828' }}>
                              <Icon name="times circle" />
                              Critical Issues ({validationResult.issues.length}):
                            </strong>
                            <ul style={{ margin: '0.3em 0 0 1.5em', color: '#db2828' }}>
                              {validationResult.issues.map((issue, index) => (
                                <li key={index}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {validationResult.warnings?.length > 0 && (
                          <div>
                            <strong style={{ color: '#f2711c' }}>
                              <Icon name="exclamation triangle" />
                              Warnings ({validationResult.warnings.length}):
                            </strong>
                            <ul style={{ margin: '0.3em 0 0 1.5em', color: '#f2711c' }}>
                              {validationResult.warnings.map((warning, index) => (
                                <li key={index}>{warning}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Message.Content>
            </Message>
          )}

          {/* Show validation loading state when validating */}
          {isEditing && validationLoading && !validationResult && (
            <Message info style={{ marginBottom: '1.5em' }}>
              <Icon name="spinner" loading />
              <Message.Content>
                <Message.Header>Validating Security Definition...</Message.Header>
                <p>Running server-side validation checks</p>
              </Message.Content>
            </Message>
          )}

          {isLoading && !currentSecurityDefinition && isEditing ? (
            <Message info>
              <Message.Header>Loading...</Message.Header>
              <p>Fetching security definition data</p>
            </Message>
          ) : (
            <Formik
              initialValues={getInitialValues()}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, isSubmitting, setFieldValue }) => {
                // Check for changes whenever values change (moved outside of render)
                checkForChanges(values);

                return (
                  <Form>
                    <Grid>
                      {/* Basic Information */}
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Header as="h3" dividing>Basic Information</Header>
                        </Grid.Column>
                      </Grid.Row>

                      <Grid.Row>
                        <Grid.Column width={8}>
                          <FormikField
                            name="name"
                            component={FormikInput}
                            label="Security Definition Name"
                            fluid
                            placeholder="e.g., opportunity-financial"
                            disabled={isEditing} // Name cannot be changed when editing
                          />
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <FormikField
                            name="version"
                            component={FormikInput}
                            label="Version"
                            fluid
                            placeholder="1.0.0"
                          />
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <FormikField
                            name="active"
                            component={FormikCheckbox}
                            label="Active"
                          />
                        </Grid.Column>
                      </Grid.Row>

                      <Grid.Row>
                        <Grid.Column width={8}>
                          <FormikField
                            name="entityType"
                            component={FormikSelect}
                            label="Entity Type"
                            options={entityTypeOptions}
                            fluid
                            placeholder="Select entity type"
                          />
                        </Grid.Column>
                        <Grid.Column width={8}>
                          <FormikField
                            name="criticality"
                            component={FormikSelect}
                            label="Criticality Level"
                            options={criticalityOptions}
                            fluid
                          />
                        </Grid.Column>
                      </Grid.Row>

                      <Grid.Row>
                        <Grid.Column width={16}>
                          <FormikField
                            name="description"
                            component={FormikTextArea}
                            label="Description"
                            placeholder="Describe the purpose and scope of this security definition"
                            rows={3}
                          />
                        </Grid.Column>
                      </Grid.Row>

                      {/* Access Control Rules */}
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Header as="h3" dividing>Access Control Rules</Header>

                          {/* JSON Drop Zone for Access Control Rules */}
                          <Segment
                            style={{
                              border: '2px dashed #ccc',
                              padding: '20px',
                              textAlign: 'center',
                              marginBottom: '20px',
                              backgroundColor: '#f9f9f9',
                              cursor: 'pointer'
                            }}
                            tabIndex={0}
                            onDrop={(e) => {
                              e.preventDefault();
                              const jsonText = e.dataTransfer.getData('text');
                              processAccessControlJson(jsonText, setFieldValue);
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDragEnter={(e) => e.preventDefault()}
                            onPaste={(e) => {
                              e.preventDefault();
                              const jsonText = e.clipboardData.getData('text');
                              processAccessControlJson(jsonText, setFieldValue);
                            }}
                            onClick={(e) => {
                              // Focus the element to enable paste functionality
                              e.currentTarget.focus();
                            }}
                          >
                            <Icon name="upload" size="large" color="grey" />
                            <Header as="h4" color="grey">
                              Drop or Paste JSON Access Control Rules Here
                            </Header>
                            <p style={{ color: '#666', margin: '10px 0' }}>
                              Drag and drop a JSON object or click here and paste (Ctrl+V) JSON containing "operations" with CREATE, READ, UPDATE, DELETE rules
                            </p>
                            <Message size="mini" style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
                              <Message.Header>Expected JSON Format:</Message.Header>
                              <pre style={{ fontSize: '11px', overflow: 'auto' }}>
                                {exampleJSONAcessControlRules}
                              </pre>
                            </Message>
                          </Segment>

                          <FieldArray name="accessControl">
                            {({ push, remove }) => (
                              <>
                                {values.accessControl && values.accessControl.length > 0 ? (
                                  values.accessControl.map((rule, index) => (
                                    <Segment key={index}>
                                      <Grid>
                                        <Grid.Row>
                                          <Grid.Column width={3}>
                                            <FormikField
                                              name={`accessControl.${index}.operation`}
                                              component={FormikSelect}
                                              label="Operation"
                                              options={operationOptions}
                                              fluid
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={4}>
                                            <FormikField
                                              name={`accessControl.${index}.requiredRoles`}
                                              component={FormikSelect}
                                              label="Required Roles"
                                              options={roleOptions}
                                              fluid
                                              multiple
                                              selection
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={4}>
                                            <FormikField
                                              name={`accessControl.${index}.description`}
                                              component={FormikInput}
                                              label="Description"
                                              fluid
                                              placeholder="Rule description"
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={4}>
                                            <FormikField
                                              name={`accessControl.${index}.advancedRules`}
                                              component={FormikSelect}
                                              label="Advanced Rules"
                                              options={advancedRulesOptions}
                                              fluid
                                              multiple
                                              selection
                                              placeholder="Select advanced security rules"
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={1} verticalAlign="bottom">
                                            <Button
                                              icon
                                              negative
                                              type="button"
                                              onClick={() => remove(index)}
                                              style={{ marginBottom: '1em' }}
                                            >
                                              <Icon name="trash" />
                                            </Button>
                                          </Grid.Column>
                                        </Grid.Row>
                                      </Grid>
                                    </Segment>
                                  ))
                                ) : (
                                  <Message info>
                                    <p>No access control rules defined. Click the button below to add a rule or drop JSON above.</p>
                                  </Message>
                                )}
                                <Button
                                  type="button"
                                  onClick={() => {
                                    const newRule = {
                                      operation: 'READ',
                                      requiredRoles: ['user'],
                                      conditions: [],
                                      description: '',
                                      advancedRules: getAdvancedRulesDefaults('READ', values.criticality || 'MEDIUM')
                                    };
                                    push(newRule);
                                  }}
                                  icon
                                  labelPosition="left"
                                  primary
                                  style={{ marginTop: '1em' }}
                                >
                                  <Icon name="plus" />
                                  Add Access Control Rule
                                </Button>
                              </>
                            )}
                          </FieldArray>
                        </Grid.Column>
                      </Grid.Row>

                      {/* Validation Rules */}
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Header as="h3" dividing>Validation Rules</Header>

                          {/* JSON Drop Zone for Validation Rules */}
                          <Segment
                            style={{
                              border: '2px dashed #ccc',
                              padding: '20px',
                              textAlign: 'center',
                              marginBottom: '20px',
                              backgroundColor: '#f9f9f9',
                              cursor: 'pointer'
                            }}
                            tabIndex={0}
                            onDrop={(e) => {
                              e.preventDefault();
                              const jsonText = e.dataTransfer.getData('text');
                              processValidationJson(jsonText, setFieldValue);
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDragEnter={(e) => e.preventDefault()}
                            onPaste={(e) => {
                              e.preventDefault();
                              const jsonText = e.clipboardData.getData('text');
                              processValidationJson(jsonText, setFieldValue);
                            }}
                            onClick={(e) => {
                              // Focus the element to enable paste functionality
                              e.currentTarget.focus();
                            }}
                          >
                            <Icon name="upload" size="large" color="grey" />
                            <Header as="h4" color="grey">
                              Drop or Paste JSON Validation Rules Here
                            </Header>
                            <p style={{ color: '#666', margin: '10px 0' }}>
                              Drag and drop a JSON object or click here and paste (Ctrl+V) JSON containing "validations" with field validation rules
                            </p>
                            <Message size="mini" style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
                              <Message.Header>Expected JSON Format:</Message.Header>
                              <pre style={{ fontSize: '11px', overflow: 'auto' }}>
                                {exampleJSONValidationRulesFormat}
                              </pre>
                            </Message>
                          </Segment>

                          <FieldArray name="validationRules">
                            {({ push, remove }) => (
                              <>
                                {values.validationRules && values.validationRules.length > 0 ? (
                                  values.validationRules.map((rule, index) => (
                                    <Segment key={index}>
                                      <Grid>
                                        <Grid.Row>
                                          <Grid.Column width={3}>
                                            <FormikField
                                              name={`validationRules.${index}.field`}
                                              component={FormikInput}
                                              label="Field"
                                              fluid
                                              placeholder="e.g., amount"
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={3}>
                                            <FormikField
                                              name={`validationRules.${index}.type`}
                                              component={FormikSelect}
                                              label="Rule Type"
                                              options={validationTypeOptions}
                                              fluid
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={4}>
                                            <FormikField
                                              name={`validationRules.${index}.parameters`}
                                              component={FormikInput}
                                              label="Parameters (JSON)"
                                              fluid
                                              placeholder='{"min": 0, "max": 1000000}'
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={5}>
                                            <FormikField
                                              name={`validationRules.${index}.message`}
                                              component={FormikInput}
                                              label="Error Message"
                                              fluid
                                              placeholder="Validation error message"
                                            />
                                          </Grid.Column>
                                          <Grid.Column width={1} verticalAlign="bottom">
                                            <Button
                                              icon
                                              negative
                                              type="button"
                                              onClick={() => remove(index)}
                                              style={{ marginBottom: '1em' }}
                                            >
                                              <Icon name="trash" />
                                            </Button>
                                          </Grid.Column>
                                        </Grid.Row>
                                      </Grid>
                                    </Segment>
                                  ))
                                ) : (
                                  <Message info>
                                    <p>No validation rules defined. Click the button below to add a rule or drop JSON above.</p>
                                  </Message>
                                )}
                                <Button
                                  type="button"
                                  onClick={() => push({
                                    field: '',
                                    type: 'REQUIRED',
                                    parameters: '',
                                    message: ''
                                  })}
                                  icon
                                  labelPosition="left"
                                  primary
                                  style={{ marginTop: '1em' }}
                                >
                                  <Icon name="plus" />
                                  Add Validation Rule
                                </Button>
                              </>
                            )}
                          </FieldArray>
                        </Grid.Column>
                      </Grid.Row>

                      {/* Audit Configuration */}
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Header as="h3" dividing>Audit Configuration</Header>
                        </Grid.Column>
                      </Grid.Row>

                      <Grid.Row>
                        <Grid.Column width={4}>
                          <FormikField
                            name="auditConfig.enabled"
                            component={FormikCheckbox}
                            label="Enable Auditing"
                          />
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <FormikField
                            name="auditConfig.level"
                            component={FormikSelect}
                            label="Audit Level"
                            options={auditLevelOptions}
                            fluid
                            disabled={!values.auditConfig?.enabled}
                          />
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <FormikField
                            name="auditConfig.retentionDays"
                            component={FormikInput}
                            label="Retention Days"
                            type="number"
                            fluid
                            disabled={!values.auditConfig?.enabled}
                          />
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <FormikField
                            name="auditConfig.includeSensitiveData"
                            component={FormikCheckbox}
                            label="Include Sensitive Data"
                            disabled={!values.auditConfig?.enabled}
                          />
                        </Grid.Column>
                      </Grid.Row>

                      {/* Tags */}
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <FormikField
                            name="tags"
                            component={FormikInput}
                            label="Tags (comma-separated)"
                            fluid
                            placeholder="e.g., financial, critical, audit-required"
                          />
                        </Grid.Column>
                      </Grid.Row>

                      {/* Messages */}
                      {localSuccess && (
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <Message positive>
                              <Message.Header>Success</Message.Header>
                              <p>{localSuccess}</p>
                            </Message>
                          </Grid.Column>
                        </Grid.Row>
                      )}

                      {localError && (
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <Message negative>
                              <Message.Header>Error</Message.Header>
                              <p>{localError}</p>
                            </Message>
                          </Grid.Column>
                        </Grid.Row>
                      )}

                      {/* Submit Button */}
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Button.Group>
                            <Button
                              primary
                              type="submit"
                              loading={formLoading}
                              disabled={isSubmitting || (isEditing && !hasChanges)}
                            >
                              {isEditing ? 'Update' : 'Create'} Security Definition
                            </Button>
                            <Button.Or />
                            <Button type="button" onClick={handleCancel}>
                              Cancel
                            </Button>
                          </Button.Group>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          )}
        </Segment>
      </Container>
      {AuthStdFooter()}
    </div>
  );
};

export default SecurityDefinitionForm;
